import { useEffect, useState } from "react";
import { askGet } from "../utils/ask/askRequest";
import { AiCapabilityMap } from "../types/Ai";

const TTL_MS = 60 * 1000;
const cache = new Map<string, { value: AiCapabilityMap | null; at: number }>();

const load = async (page: string | null): Promise<AiCapabilityMap | null> => {
    const key = page ?? "";
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) return hit.value;
    let value: AiCapabilityMap | null = null;
    try {
        const response = await askGet<AiCapabilityMap>(`ai/capabilities${page ? `?page=${encodeURIComponent(page)}` : ""}`);
        value = response?.status === 200 && response.body ? response.body : null;
    } catch {
        value = null;
    }
    // A failed read isn't cached, so the next open tries again.
    if (value) cache.set(key, { value, at: Date.now() });
    return value;
};

/** Forget the cached lists (tests; after a role change). */
export const clearAiCapabilitiesCache = (): void => cache.clear();

/**
 * What Ask can do for the signed-in user (ai/capabilities), ranked by their
 * roles and permissions: only areas and questions their own role can answer.
 * Null while loading or when it can't be read — callers then show no
 * sample questions at all, never a generic list.
 * @param page - The kind of page the user is on ("mission"), for "On this page".
 * @param active - Load only when needed (e.g. the drawer is open).
 */
export const useAiCapabilities = (page: string | null, active: boolean): AiCapabilityMap | null => {
    const [map, setMap] = useState<AiCapabilityMap | null>(null);

    useEffect(() => {
        if (!active) return;
        let isMounted = true;
        load(page).then((value) => { if (isMounted) setMap(value); });
        return () => { isMounted = false; };
    }, [page, active]);

    return map;
};
